import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DollarSignIcon, BuildingIcon, CalendarIcon, FileTextIcon, CheckCircleIcon } from 'lucide-react';
export function BillFinancingWizard() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    loanPackage: '',
    financingAmount: '',
    requestedTerm: '30',
    purpose: '',
    additionalNotes: ''
  });
  // Mock bill data
  const bill = {
    id: id,
    billNumber: 'BILL-2024-002',
    supplierName: 'Spice Traders Co',
    amount: 87500,
    dueDate: '2024-02-20'
  };
  const dfccLoanPackages = [{
    id: 'standard',
    name: 'Standard Financing',
    interestRate: '12%',
    maxTerm: '90 days',
    processingTime: '48 hours',
    description: 'Ideal for regular business operations with flexible terms'
  }, {
    id: 'premium',
    name: 'Premium Financing',
    interestRate: '10.5%',
    maxTerm: '120 days',
    processingTime: '24 hours',
    description: 'Lower rates for established businesses with priority processing'
  }, {
    id: 'express',
    name: 'Express Financing',
    interestRate: '14%',
    maxTerm: '60 days',
    processingTime: '12 hours',
    description: 'Quick approval for urgent cash flow needs'
  }, {
    id: 'sme',
    name: 'SME Growth Package',
    interestRate: '11%',
    maxTerm: '90 days',
    processingTime: '36 hours',
    description: 'Special rates for small and medium enterprises'
  }];
  const handleSubmit = () => {
    console.log('Submitting financing request:', {
      billId: id,
      ...formData
    });
    navigate('/bills');
  };
  const selectedPackage = dfccLoanPackages.find(p => p.id === formData.loanPackage);
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-4141">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-4142">
        <button onClick={() => navigate(`/bill-detail/${id}`)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-4143">
          <ArrowLeftIcon size={20} data-id="element-4144" />
          Back to Bill
        </button>
      </div>
      {/* Progress Steps */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4145">
        <div className="flex items-center justify-between mb-8" data-id="element-4146">
          <div className="flex-1" data-id="element-4147">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-4148">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-4149">
                1
              </div>
              <div className="ml-3" data-id="element-4150">
                <div className="text-sm font-medium" data-id="element-4151">Bill Details</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-4152"></div>
          <div className="flex-1" data-id="element-4153">
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-4154">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-4155">
                2
              </div>
              <div className="ml-3" data-id="element-4156">
                <div className="text-sm font-medium" data-id="element-4157">Select Provider</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-4158"></div>
          <div className="flex-1" data-id="element-4159">
            <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-4160">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-4161">
                3
              </div>
              <div className="ml-3" data-id="element-4162">
                <div className="text-sm font-medium" data-id="element-4163">Financing Terms</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 4 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-4164"></div>
          <div className="flex-1" data-id="element-4165">
            <div className={`flex items-center ${step >= 4 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-4166">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-4167">
                4
              </div>
              <div className="ml-3" data-id="element-4168">
                <div className="text-sm font-medium" data-id="element-4169">Review & Submit</div>
              </div>
            </div>
          </div>
        </div>
        {/* Step 1: Bill Details */}
        {step === 1 && <div className="space-y-6" data-id="element-4170">
            <div data-id="element-4171">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4172">
                Bill Information
              </h2>
              <p className="text-gray-600" data-id="element-4173">
                Review the bill details for financing
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-4174">
              <div className="grid grid-cols-2 gap-4" data-id="element-4175">
                <div data-id="element-4176">
                  <div className="text-sm text-gray-500" data-id="element-4177">Bill Number</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-4178">
                    {bill.billNumber}
                  </div>
                </div>
                <div data-id="element-4179">
                  <div className="text-sm text-gray-500" data-id="element-4180">Supplier</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-4181">
                    {bill.supplierName}
                  </div>
                </div>
                <div data-id="element-4182">
                  <div className="text-sm text-gray-500" data-id="element-4183">Due Date</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-4184">
                    {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                  </div>
                </div>
                <div data-id="element-4185">
                  <div className="text-sm text-gray-500" data-id="element-4186">Bill Amount</div>
                  <div className="text-2xl font-bold text-gray-900" data-id="element-4187">
                    RM {bill.amount.toLocaleString('en-MY')}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-4188">
              <div className="flex items-start gap-3" data-id="element-4189">
                <DollarSignIcon size={20} className="text-blue-600 mt-0.5" data-id="element-4190" />
                <div className="text-sm text-blue-800" data-id="element-4191">
                  <div className="font-medium mb-1" data-id="element-4192">
                    Bill Financing Benefits
                  </div>
                  <ul className="list-disc list-inside space-y-1" data-id="element-4193">
                    <li data-id="element-4194">Improve cash flow by paying suppliers early</li>
                    <li data-id="element-4195">Maintain good supplier relationships</li>
                    <li data-id="element-4196">Take advantage of early payment discounts</li>
                    <li data-id="element-4197">Flexible repayment terms</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-4198">
              <button onClick={() => setStep(2)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-4199">
                Continue
              </button>
            </div>
          </div>}
        {/* Step 2: Select Provider */}
        {step === 2 && <div className="space-y-6" data-id="element-4200">
            <div data-id="element-4201">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4202">
                Select RHB Bank Financing Package
              </h2>
              <p className="text-gray-600" data-id="element-4203">
                Choose a financing package that best suits your needs
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-id="element-4204">
              <div className="flex items-start gap-3" data-id="element-4205">
                <BuildingIcon size={20} className="text-blue-600 mt-0.5" data-id="element-4206" />
                <div data-id="element-4207">
                  <div className="font-semibold text-blue-900 mb-1" data-id="element-4208">
                    RHB Bank - Your Trusted Financial Partner
                  </div>
                  <div className="text-sm text-blue-800" data-id="element-4209">
                    As a valued RHB Bank customer, you have access to exclusive
                    financing packages tailored for your business needs.
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4" data-id="element-4210">
              {dfccLoanPackages.map(pkg => <label key={pkg.id} className={`block p-6 border-2 rounded-lg cursor-pointer transition-all ${formData.loanPackage === pkg.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} data-id="element-4211">
                  <input type="radio" name="loanPackage" value={pkg.id} checked={formData.loanPackage === pkg.id} onChange={e => setFormData({
              ...formData,
              loanPackage: e.target.value
            })} className="sr-only" data-id="element-4212" />
                  <div className="flex items-start justify-between" data-id="element-4213">
                    <div className="flex-1" data-id="element-4214">
                      <div className="flex items-center gap-2 mb-2" data-id="element-4215">
                        <div className="text-lg font-semibold text-gray-900" data-id="element-4216">
                          {pkg.name}
                        </div>
                        {formData.loanPackage === pkg.id && <CheckCircleIcon size={20} className="text-blue-600" data-id="element-4217" />}
                      </div>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-4218">
                        {pkg.description}
                      </p>
                      <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-4219">
                        <div data-id="element-4220">
                          <div className="text-gray-500" data-id="element-4221">Interest Rate</div>
                          <div className="font-medium text-gray-900" data-id="element-4222">
                            {pkg.interestRate}
                          </div>
                        </div>
                        <div data-id="element-4223">
                          <div className="text-gray-500" data-id="element-4224">Max Term</div>
                          <div className="font-medium text-gray-900" data-id="element-4225">
                            {pkg.maxTerm}
                          </div>
                        </div>
                        <div data-id="element-4226">
                          <div className="text-gray-500" data-id="element-4227">Processing Time</div>
                          <div className="font-medium text-gray-900" data-id="element-4228">
                            {pkg.processingTime}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>)}
            </div>
            <div className="flex justify-between" data-id="element-4229">
              <button onClick={() => setStep(1)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4230">
                Back
              </button>
              <button onClick={() => setStep(3)} disabled={!formData.loanPackage} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4231">
                Continue
              </button>
            </div>
          </div>}
        {/* Step 3: Financing Terms */}
        {step === 3 && <div className="space-y-6" data-id="element-4232">
            <div data-id="element-4233">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4234">
                Financing Terms
              </h2>
              <p className="text-gray-600" data-id="element-4235">
                Specify the financing amount and terms
              </p>
            </div>
            <div className="space-y-6" data-id="element-4236">
              <div data-id="element-4237">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4238">
                  Financing Amount (MYR) *
                </label>
                <input type="number" value={formData.financingAmount} onChange={e => setFormData({
              ...formData,
              financingAmount: e.target.value
            })} placeholder="Enter amount" max={bill.amount} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4239" />
                <div className="mt-1 text-sm text-gray-500" data-id="element-4240">
                  Maximum: RM {bill.amount.toLocaleString('en-MY')}
                </div>
              </div>
              <div data-id="element-4241">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4242">
                  Requested Term (Days) *
                </label>
                <select value={formData.requestedTerm} onChange={e => setFormData({
              ...formData,
              requestedTerm: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4243">
                  <option value="15" data-id="element-4244">15 days</option>
                  <option value="30" data-id="element-4245">30 days</option>
                  <option value="45" data-id="element-4246">45 days</option>
                  <option value="60" data-id="element-4247">60 days</option>
                  <option value="90" data-id="element-4248">90 days</option>
                </select>
              </div>
              <div data-id="element-4249">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4250">
                  Purpose of Financing *
                </label>
                <select value={formData.purpose} onChange={e => setFormData({
              ...formData,
              purpose: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4251">
                  <option value="" data-id="element-4252">Select purpose</option>
                  <option value="early_payment" data-id="element-4253">Early Payment Discount</option>
                  <option value="cash_flow" data-id="element-4254">Cash Flow Management</option>
                  <option value="supplier_relationship" data-id="element-4255">
                    Supplier Relationship
                  </option>
                  <option value="other" data-id="element-4256">Other</option>
                </select>
              </div>
              <div data-id="element-4257">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4258">
                  Additional Notes
                </label>
                <textarea value={formData.additionalNotes} onChange={e => setFormData({
              ...formData,
              additionalNotes: e.target.value
            })} rows={4} placeholder="Any additional information..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4259" />
              </div>
            </div>
            <div className="flex justify-between" data-id="element-4260">
              <button onClick={() => setStep(2)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4261">
                Back
              </button>
              <button onClick={() => setStep(4)} disabled={!formData.financingAmount || !formData.requestedTerm || !formData.purpose} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4262">
                Review Request
              </button>
            </div>
          </div>}
        {/* Step 4: Review & Submit */}
        {step === 4 && <div className="space-y-6" data-id="element-4263">
            <div data-id="element-4264">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4265">
                Review Financing Request
              </h2>
              <p className="text-gray-600" data-id="element-4266">
                Please review your financing request before submitting
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-6" data-id="element-4267">
              <div data-id="element-4268">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-4269">
                  BILL INFORMATION
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-4270">
                  <div data-id="element-4271">
                    <div className="text-sm text-gray-500" data-id="element-4272">Bill Number</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4273">
                      {bill.billNumber}
                    </div>
                  </div>
                  <div data-id="element-4274">
                    <div className="text-sm text-gray-500" data-id="element-4275">Supplier</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4276">
                      {bill.supplierName}
                    </div>
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-4277">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-4278">
                  FINANCING DETAILS
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-4279">
                  <div data-id="element-4280">
                    <div className="text-sm text-gray-500" data-id="element-4281">Bank</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4282">
                      RHB Bank
                    </div>
                  </div>
                  <div data-id="element-4283">
                    <div className="text-sm text-gray-500" data-id="element-4284">Package</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4285">
                      {selectedPackage?.name}
                    </div>
                  </div>
                  <div data-id="element-4286">
                    <div className="text-sm text-gray-500" data-id="element-4287">Interest Rate</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4288">
                      {selectedPackage?.interestRate}
                    </div>
                  </div>
                  <div data-id="element-4289">
                    <div className="text-sm text-gray-500" data-id="element-4290">Processing Time</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4291">
                      {selectedPackage?.processingTime}
                    </div>
                  </div>
                  <div data-id="element-4292">
                    <div className="text-sm text-gray-500" data-id="element-4293">
                      Financing Amount
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4294">
                      RM{' '}
                      {parseFloat(formData.financingAmount).toLocaleString('en-MY')}
                    </div>
                  </div>
                  <div data-id="element-4295">
                    <div className="text-sm text-gray-500" data-id="element-4296">Term</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4297">
                      {formData.requestedTerm} days
                    </div>
                  </div>
                  <div data-id="element-4298">
                    <div className="text-sm text-gray-500" data-id="element-4299">Purpose</div>
                    <div className="text-base font-medium text-gray-900 capitalize" data-id="element-4300">
                      {formData.purpose.replace('_', ' ')}
                    </div>
                  </div>
                </div>
              </div>
              {formData.additionalNotes && <div className="pt-4 border-t border-gray-200" data-id="element-4301">
                  <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4302">
                    ADDITIONAL NOTES
                  </h3>
                  <p className="text-sm text-gray-700" data-id="element-4303">
                    {formData.additionalNotes}
                  </p>
                </div>}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-4304">
              <div className="flex items-start gap-3" data-id="element-4305">
                <FileTextIcon size={20} className="text-blue-600 mt-0.5" data-id="element-4306" />
                <div className="text-sm text-blue-800" data-id="element-4307">
                  By submitting this request, you agree to the financing terms
                  and conditions set by RHB Bank for the {selectedPackage?.name}
                  . The request will be processed within{' '}
                  {selectedPackage?.processingTime}.
                </div>
              </div>
            </div>
            <div className="flex justify-between" data-id="element-4308">
              <button onClick={() => setStep(3)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4309">
                Back
              </button>
              <button onClick={handleSubmit} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-4310">
                Submit Financing Request
              </button>
            </div>
          </div>}
      </div>
    </div>;
}